import React, { FC } from "react";
import {
  Text,
  View,
  StyleSheet,
  TouchableOpacity,
  Linking,
  Platform,
} from "react-native";

import { AuthContext } from "../../contexts/auth/Store";
import { SafeJSONObject, DateFormatter } from "../../constants/Utils";

import { AnalyticsService } from "../../constants/Analytics";
import StyledIcon from "../StyledIcon";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import usePhoneRep from "../common/PhoneRep";
import OrderStatus from "./OrderStatus";
import useOrderTotal from "../../hooks/useOrderTotal";
import config from "../../constants/Config";
import * as Sharing from "expo-sharing";
import { formatCurrency } from "../../utils/money";
import useFetch from "../../hooks/useFetch";
import { ILedger, LedgerHead, LedgerSubhead } from "../../interfaces/ledger";
import { ledgerService } from "../../services/LedgerService";
import { IOrderGroup } from "../../interfaces/order";
import { useNavigation } from "@react-navigation/native";
import { OrderContext } from "../../contexts/order/Store";

const analytics = new AnalyticsService();

interface IProps {
  order: IOrderGroup;
  readOnly?: boolean;
  callRep?: boolean;
  showAddress?: boolean;
  hideConfirmButton?: boolean;
  confirmAction?: Function;
  screenCaptureRef?: any;
}

const OrderGroupCard: FC<IProps> = ({
  order,
  readOnly,
  callRep,
  showAddress,
  hideConfirmButton,
  confirmAction,
  screenCaptureRef,
}) => {
  const navigation = useNavigation();

  const { state: order_state } = React.useContext(OrderContext);
  const auth = React.useContext(AuthContext);
  const buyer = auth.state.buyerData;
  const [phoneRep, loadingPhoneRep] = usePhoneRep();

  const gotoOrder = () => {
    navigation.navigate({
      name: "OrderInvoice",
      params: { order_id: order._id },
    });
  };
  const placeCall = () => {
    analytics.logEvent("Called Sales Rep", buyer, {
      order_id: order?._id,
      order_ref: order?.ref,
      amount: order?.amount,
      tag: order?.tag,
    });
    Linking.openURL("tel:" + phoneRep);
  };
  const openModal = () => {
    if (confirmAction && typeof confirmAction === "function") {
      confirmAction(order);
    }
  };

  const [ledger] = useFetch<ILedger>(
    ledgerService,
    "fetchByOwnerSpecific",
    [
      {
        head: LedgerHead.Buyer,
        subhead: LedgerSubhead.Wallet,
        owner: buyer?._id,
      },
    ],
    !!buyer?._id && order_state?.latest_order?._id === order?._id
  );

  const total = React.useMemo<number>(() => {
    const { total: _total } = useOrderTotal(order, false);
    // return _total + ((ledger?.balance * -1) || 0);
    return _total
  }, [order, ledger]);

  const shareInvoice = () => {
    // https://stackoverflow.com/a/9039885/11329395
    function iOS() {
      return (
        [
          "iPad Simulator",
          "iPhone Simulator",
          "iPod Simulator",
          "iPad",
          "iPhone",
          "iPod",
        ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document) ||
        // fails on iPad iOS 13
        (!window.MSStream && /iPad|iPhone|iPod/.test(navigator.userAgent))
      );
    }

    if (Platform.OS === "web") {
      let url = "sms:";
      // https://stackoverflow.com/a/19126326/11329395
      url += iOS() ? "&" : "?";
      url += `body=Suplias invoice no.${order?.ref}: Visit ${config.share_url}/invoice/${order?.ref}`;

      Linking.openURL(`${encodeURI(`${url}`)}`);
    }

    if (Platform.OS === "android") {
      // let's do some magic
      captureView();
    }
  };

  const captureView = async () => {
    try {
      if (screenCaptureRef.current) {
        let imageResult = await screenCaptureRef.current.capture();
        shareView(imageResult);
      }
    } catch (err) {
      analytics.logError(err);
    }
  };

  const shareView = async (url: string) => {
    try {
      await Sharing.shareAsync(url, {
        dialogTitle: "Suplias Invoice",
      });
    } catch (err) {
      analytics.logError(err);
    }
  };

  return (
    <>
      <TouchableOpacity
        style={styles.card}
        activeOpacity={readOnly ? 1 : 0.95}
        disabled={readOnly === true}
        onPress={gotoOrder}
      >
        <View style={styles.cardBody}>
          <View
            style={{
              flexShrink: 1,
            }}
          >
            <View>
              <Text style={styles.referenceText}>Invoice #{order?.ref}</Text>
            </View>
            <View style={{ flexDirection: "row" }}>
              <Text style={styles.dateText}>
                {DateFormatter(order?.modified)}
              </Text>
            </View>
            {showAddress && (
              <Text style={styles.address}>{buyer?.address}</Text>
            )}
          </View>
          <View
            style={{
              flexGrow: 1,
              alignItems: "flex-end",
            }}
          >
            <Text style={styles.priceText}>{formatCurrency(total)}</Text>
          </View>
        </View>
        <View style={styles.cardFooter}>
          <OrderStatus type={order?.tag} />
          <View style={styles.actionsContainer}>
            {order?.tag === "completed" &&
            !SafeJSONObject(order?.meta)?.confirmed &&
            !hideConfirmButton ? (
              <>
                <TouchableOpacity
                  onPress={openModal}
                  style={styles.actionButton}
                >
                  <Text style={styles.linkText}>Confirm</Text>
                </TouchableOpacity>
                <Text style={{ marginHorizontal: 10 }}>&middot;</Text>
              </>
            ) : null}
            {readOnly === true ? (
              // ? <Text style={styles.linkText}></Text>
              <TouchableOpacity
                onPress={shareInvoice}
                style={styles.actionButton}
              >
                <Text style={styles.linkText}>SHARE</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity onPress={gotoOrder} style={styles.actionButton}>
                <Text style={styles.linkText}>VIEW DETAILS</Text>
              </TouchableOpacity>
            )}

            {callRep !== true ? null : (
              <TouchableOpacity
                style={[styles.callRepContainer, styles.actionButton]}
                onPress={placeCall}
                disabled={loadingPhoneRep}
              >
                <StyledIcon
                  iconName="call"
                  iconColor={Colors.whiteColor}
                  fillColor={Colors.tintColor}
                  size={16}
                  style={{
                    alignSelf: "center",
                    marginRight: 4,
                  }}
                />
                <Text style={styles.callRepText}>CALL REP</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </TouchableOpacity>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    marginTop: 10,
    backgroundColor: Colors.whiteColor,
    elevation: 1,
  },
  cardBody: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  cardFooter: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopColor: "#F6F6F6",
    borderTopWidth: 1,
    borderStyle: "solid",
  },
  referenceText: {
    ...FontStyles.dark,
    ...FontStyles.size13,
    ...FontStyles.lh18,
    ...FontStyles.semibold,
  },
  manufacturerText: {
    color: Colors.shades._7,
    ...FontStyles.size13,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    marginTop: 5,
  },
  dateText: {
    color: Colors.shades._7,
    ...FontStyles.size13,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    marginTop: 5,
  },
  priceText: {
    ...FontStyles.dark,
    ...FontStyles.size13,
    ...FontStyles.lh18,
    ...FontStyles.semibold,
  },
  linkText: {
    color: Colors.tintColor,
    ...FontStyles.size13,
    ...FontStyles.lh15,
    ...FontStyles.semibold,
    // minHeight: 38,
    textAlignVertical: "center",
    textTransform: "uppercase",
    alignSelf: "center",
  },
  callRepContainer: {
    flexDirection: "row",
    alignItems: "center",
    // marginVertical: 10,
    height: 38,
  },
  address: {
    ...FontStyles.size14,
    ...FontStyles.lh20,
    ...FontStyles.normal,
    marginTop: 10,
  },
  callRepText: {
    ...FontStyles.size12,
    ...FontStyles.lh15,
    ...FontStyles.default,
    ...FontStyles.semibold,
    textAlignVertical: "center",
  },
  actionsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  actionButton: {
    paddingVertical: 10,
    paddingHorizontal: 6,
    alignSelf: "center",
  },
});

export default OrderGroupCard;
